import { FC, useEffect, useMemo } from "react";

import { useAppSelector, handleCompany } from "../../store";

import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";

import { Restricted } from "../../components/Restricted";

import { VviinnForms } from "../../forms";
import { QueryableField, FormType } from "../../forms/types";
import { DetailedWrapper } from "../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { Section } from "../../ui/layout/detailedPages/Section";
import { Fields } from "../../ui/layout/detailedPages/Fields";
import { SlideOverFieldNamedRow } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { NoDataMessage } from "../../ui/layout/detailedPages/NoDataMessage";
import { Plans } from "../../company/details/Billing/Plans";
import { Billing } from "../../company/details/Billing/Billing";
import { CompanyMembers } from "../../user/role/CompanyMembers";
import { SubSectionWithStyles } from "../../ui/layout";

export const CompanyDetails: FC<React.PropsWithChildren> = () => {
  const handlerParams = useRequestHandlers();
  const { handleUpdateCompanyField, handleDeleteCompany, fetchCompany } =
    useMemo(() => handleCompany(handlerParams), [handlerParams]);
  const notify = handlerParams.notify;

  const currentCompany = useAppSelector((state) => state.company.company);

  const currentCompanyId = currentCompany?.id;
  const companyName = currentCompany?.name ?? "Unknown company";

  const authenticationAction = currentCompany?.authentication?.actions?.get;

  const contractAction = currentCompany?.contract?.actions?.get;
  const warningInContract = currentCompany?.contract?.warning;

  useEffect(() => {
    if (!currentCompanyId) return;
    fetchCompany(currentCompanyId);
  }, [currentCompanyId]);

  const updateName = async (value: string) => {
    if (!currentCompanyId) return false;
    return await handleUpdateCompanyField("name", currentCompanyId, value);
  };

  const deleteCompany = async () => {
    if (!currentCompanyId) return false;
    return await handleDeleteCompany(currentCompanyId);
  };

  const customActionText = useMemo(
    () => `Do you want to delete ${currentCompany?.name}?`,
    [currentCompany?.name],
  );

  return (
    <DetailedWrapper
      deleteAction={deleteCompany}
      detailedPageType="detail"
      detailedPage="company"
      customActionText={customActionText}
      backLink="/companies"
    >
      <Restricted permissions="company:name:view">
        <SubSectionWithStyles title="General information" isSubHeader={false}>
          <SlideOverFieldNamedRow
            namedRowTitle="Company"
            permissionsGroup="company:name"
            onAccept={updateName}
            slideOverValue={companyName}
          />
        </SubSectionWithStyles>
      </Restricted>
      <Restricted permissions="company:members:view">
        {currentCompany && <CompanyMembers currentCompany={currentCompany} />}
      </Restricted>
      {authenticationAction && (
        <Restricted permissions="company:authentication_mode:view">
          <Section title="Authentication">
            <VviinnForms
              formType={authenticationAction.formType as FormType}
              companyId={currentCompanyId}
              requestType={authenticationAction.requestType}
              showMainTitle={false}
              actionId={authenticationAction.id}
            />
          </Section>
        </Restricted>
      )}
      <Restricted permissions="company:contract:view">
        <Section title="Contract">
          {contractAction && !warningInContract ? (
            <VviinnForms
              formType={contractAction.formType as FormType}
              select={contractAction.select as QueryableField[]}
              companyId={contractAction.parameters.form.companyId}
              id={contractAction.parameters.form.id}
              requestType={contractAction.requestType}
              showMainTitle={false}
              actionId={contractAction.id}
              onSuccess={() => notify("Success", "Contract updated")}
            />
          ) : (
            <Fields>
              <NoDataMessage>{warningInContract}</NoDataMessage>
            </Fields>
          )}
        </Section>
      </Restricted>

      <Plans />
      <Billing />
    </DetailedWrapper>
  );
};
